import {
  IAgentWorkflow,
  IOutboundPipelineStats,
  IOutboundProspect,
  IWorkflowRun
} from "~/types/agent-os"
import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"

export async function getAgentWorkflows(
  tokenInfo: ITokenInfo
): Promise<{ data: IAgentWorkflow[] }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<{ data: IAgentWorkflow | null }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function createAgentWorkflow(
  tokenInfo: ITokenInfo,
  payload: Partial<IAgentWorkflow>
): Promise<{ data: IAgentWorkflow }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function updateAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string,
  payload: Partial<IAgentWorkflow>
): Promise<{ data: IAgentWorkflow }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}`

  return await axiosInstance({
    method: "PUT",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: payload
  })
}

export async function deleteAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<{ data: { success: boolean } }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}`

  return await axiosInstance({
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function toggleAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string,
  isEnabled: boolean
): Promise<{ data: IAgentWorkflow }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/toggle`

  return await axiosInstance({
    method: "PATCH",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: { is_enabled: isEnabled }
  })
}

export async function triggerAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string,
  data?: Record<string, any>
): Promise<{ data: IWorkflowRun }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/run`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: data || {}
  })
}

export async function testRunAgentWorkflow(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<{ data: IWorkflowRun }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/test-run`

  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getAgentWorkflowRuns(
  tokenInfo: ITokenInfo,
  workflowId: string,
  limit: number = 10,
  offset: number = 0
): Promise<{ data: IWorkflowRun[] }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/runs`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    params: { limit, offset }
  })
}

export async function startOutboundRun(
  tokenInfo: ITokenInfo,
  workflowId: string,
  config?: Record<string, any>,
  selectedAccountIds?: string[]
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/outbound-run`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    // RC1: when the user narrowed the account selection, send the ids so the
    // backend scopes stakeholder discovery to exactly those accounts (incl.
    // ones outside the run's default top-20). Omitted/empty ⇒ top-20 default.
    data: {
      config: config || {},
      ...(selectedAccountIds && selectedAccountIds.length > 0
        ? { selected_account_ids: selectedAccountIds }
        : {})
    }
  })
}

export async function getOutboundRunStatus(
  tokenInfo: ITokenInfo,
  workflowId: string,
  runId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/runs/${runId}/status`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getLatestOutboundRun(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/latest-run`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getOutboundStakeholders(
  tokenInfo: ITokenInfo,
  workflowId: string,
  runId?: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    params: runId ? { run_id: runId } : undefined
  })
}

export async function createOutboundStakeholder(
  tokenInfo: ITokenInfo,
  workflowId: string,
  payload: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: payload
  })
}

export async function deleteOutboundStakeholder(
  tokenInfo: ITokenInfo,
  workflowId: string,
  stakeholderId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders/${stakeholderId}`
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function patchOutboundStakeholder(
  tokenInfo: ITokenInfo,
  workflowId: string,
  stakeholderId: string,
  patch: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/stakeholders/${stakeholderId}`
  return await axiosInstance({
    method: "PATCH",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: patch
  })
}

export async function triggerOutboundMonitorTick(
  tokenInfo: ITokenInfo,
  workflowId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/monitor-tick`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getOutboundReplies(
  tokenInfo: ITokenInfo,
  workflowId: string,
  limit: number = 200,
  offset: number = 0
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/${workflowId}/replies`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    params: { limit, offset }
  })
}

export async function getOutboundTasks(
  tokenInfo: ITokenInfo,
  workflowId?: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    params: workflowId ? { workflow_id: workflowId } : undefined
  })
}

export async function createOutboundTask(
  tokenInfo: ITokenInfo,
  payload: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks`
  return await axiosInstance({
    method: "POST",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: payload
  })
}

export async function patchOutboundTask(
  tokenInfo: ITokenInfo,
  taskId: string,
  patch: Record<string, any>
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks/${taskId}`
  return await axiosInstance({
    method: "PATCH",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` },
    data: patch
  })
}

export async function deleteOutboundTask(
  tokenInfo: ITokenInfo,
  taskId: string
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/tasks/${taskId}`
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getEmailConnectionStatus(
  tokenInfo: ITokenInfo
): Promise<any> {
  const url = `tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-os/oauth/connection-status`
  return await axiosInstance({
    method: "GET",
    url,
    headers: { Authorization: `Bearer ${tokenInfo.token}` }
  })
}

export async function getOutboundProspects(
  tokenInfo: ITokenInfo
): Promise<{ data: IOutboundProspect[] }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/outbound/prospects`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getOutboundPipelineStats(
  tokenInfo: ITokenInfo
): Promise<{ data: IOutboundPipelineStats }> {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  const url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/agent-workflows/outbound/stats`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}
